import { useMemo, useState } from 'react';

import { Badge, Card, Empty, Select, Skeleton, Spin, Typography, theme } from 'antd';

import ColoredFormattedNumber from 'components/ColoredFormattedNumber';
import { Bar, BarChart as RechartsBarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { TooltipProps } from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import DashboardSegmented from 'v2/components/Pages/Dashboard/DashboardSegmented';
import { LAST_SEVEN_DAYS_COUNT } from 'v2/components/Pages/Dashboard/period';
import { Mask } from 'v2/components/Shared/ValuesVisibility';
import { useLastGains } from 'v2/hooks/useLastGains';

type BarChartProps = {
  id: string;
};

type RawBarDataset = { data: number[]; label: string };

type ChartSeries = {
  key: string;
  label: string;
  color: string;
};

type ChartRow = {
  [key: string]: string | number;
  date: string;
};

enum DateType {
  PAYMENT = '1',
  CREATION = '2'
}

enum TabKeys {
  PRODUCER = '0',
  COPRODUCER = '1',
  AFFILIATE = '2'
}

function GainsTooltip({ active, payload, label }: TooltipProps<ValueType, NameType>) {
  if (!active || !payload?.length) {
    return null;
  }

  const entry = payload[0];

  return (
    <div className='w-60 rounded-(--uizz-antd-border-radius) border border-(--uizz-antd-color-border) bg-(--uizz-antd-color-bg-container) px-3 py-2'>
      <Typography.Text strong className='block wrap-break-word'>
        {entry.name}
      </Typography.Text>
      <div className='flex items-center gap-2'>
        <Badge dot color={String(entry.color)} />
        <Typography.Text type='secondary'>{label}</Typography.Text>
        <Typography.Text>
          <Mask>
            <ColoredFormattedNumber value={Number(entry.value) || 0} />
          </Mask>
        </Typography.Text>
      </div>
    </div>
  );
}

const BarChart = ({ id }: BarChartProps) => {
  const { token } = theme.useToken();
  const [tab, setTab] = useState<string>(TabKeys.PRODUCER);
  const [dateType, setDateType] = useState<DateType>(DateType.PAYMENT);
  const { lastGains, isLoading } = useLastGains();
  const paletteColors = useMemo(
    () => [
      token.colorPrimaryActive,
      token.colorPrimary,
      token.colorPrimaryHover,
      token.colorPrimaryBorderHover,
      token.colorPrimaryBorder
    ],
    [token]
  );

  const source = useMemo(() => {
    switch (tab) {
      case TabKeys.COPRODUCER:
        return lastGains.coproducer;
      case TabKeys.AFFILIATE:
        return lastGains.affiliate;
      default:
        return lastGains.producer;
    }
  }, [tab, lastGains]);

  const rawSection = useMemo(() => {
    if (!source) {
      return null;
    }

    const section = dateType === DateType.CREATION ? source.create : source.payment;

    if (!section?.labels) {
      return section ?? null;
    }

    return {
      ...section,
      labels: section.labels.slice(-LAST_SEVEN_DAYS_COUNT),
      datasets: section.datasets?.map((dataset: RawBarDataset) => ({
        ...dataset,
        data: dataset.data.slice(-LAST_SEVEN_DAYS_COUNT)
      }))
    };
  }, [source, dateType]);

  const { series, rows } = useMemo(() => {
    if (!rawSection?.labels || !rawSection?.datasets) {
      return { series: [] as ChartSeries[], rows: [] as ChartRow[] };
    }

    const datasets = rawSection.datasets as RawBarDataset[];
    const rankedDatasets = datasets
      .map((item, index) => ({
        index,
        total: item.data.reduce((sum, value) => sum + (value || 0), 0)
      }))
      .sort((first, second) => second.total - first.total);

    const colorByDatasetIndex = rankedDatasets.reduce<Record<number, string>>((colors, item, rank) => {
      colors[item.index] = paletteColors[Math.min(rank, paletteColors.length - 1)];
      return colors;
    }, {});

    const nextSeries: ChartSeries[] = datasets.map((item, index) => ({
      key: `series-${index}`,
      label: item.label,
      color: colorByDatasetIndex[index]
    }));

    const nextRows: ChartRow[] = rawSection.labels.map((date: string, dateIndex: number) => {
      const row: ChartRow = { date };

      nextSeries.forEach((item, seriesIndex) => {
        row[item.key] = datasets[seriesIndex].data[dateIndex] || 0;
      });

      return row;
    });

    return { series: nextSeries, rows: nextRows };
  }, [paletteColors, rawSection]);

  const hasData = rows.length > 0;

  const totalPeriod = useMemo(
    () => rows.reduce((sum, row) => sum + series.reduce((rowSum, item) => rowSum + (Number(row[item.key]) || 0), 0), 0),
    [rows, series]
  );

  const totalPerDay = (row: ChartRow) => series.reduce((sum, item) => sum + (Number(row[item.key]) || 0), 0);

  return (
    <Card
      className='h-full'
      title='Ganho últimos 7 dias'
      extra={
        <div className='w-fit'>
          <Select
            className='w-full'
            size='small'
            value={dateType}
            onChange={setDateType}
            options={[
              { value: DateType.PAYMENT, label: 'Pagamento' },
              { value: DateType.CREATION, label: 'Criação' }
            ]}
          />
        </div>
      }
    >
      <div className='flex h-full flex-col'>
        <Typography.Text type='secondary'>
          Total no período:{' '}
          {isLoading ? (
            <span className='inline-block w-20'>
              <Skeleton.Input active size='small' className='w-full' />
            </span>
          ) : (
            <Mask>
              <ColoredFormattedNumber value={totalPeriod} />
            </Mask>
          )}
        </Typography.Text>

        <div className='mt-4'>
          <DashboardSegmented
            value={tab}
            onChange={value => setTab(String(value))}
            className='[&_.ant-segmented-item-label]:px-0'
            disabled={isLoading}
            options={[
              { value: TabKeys.PRODUCER, label: 'Produtor' },
              { value: TabKeys.COPRODUCER, label: 'Coprodutor' },
              { value: TabKeys.AFFILIATE, label: 'Afiliado' }
            ]}
          />
        </div>

        <div className='h-full pt-4'>
          {isLoading ? (
            <div className='flex h-full items-center justify-center'>
              <Spin />
            </div>
          ) : hasData ? (
            <div>
              <div id={id} className='h-55'>
                <ResponsiveContainer width='100%' height='100%'>
                  <RechartsBarChart
                    data={rows}
                    className='[&_.recharts-cartesian-axis-tick-value]:fill-(--uizz-antd-color-text-secondary)'
                  >
                    <CartesianGrid vertical={false} stroke={token.colorBorderSecondary} />
                    <XAxis dataKey='date' fontSize={8} axisLine={false} tickLine={false} />
                    <YAxis fontSize={9} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip shared={false} content={<GainsTooltip />} />
                    {series.map(item => (
                      <Bar
                        key={item.key}
                        dataKey={item.key}
                        name={item.label}
                        fill={item.color}
                        stackId='gains'
                        isAnimationActive={false}
                      />
                    ))}
                  </RechartsBarChart>
                </ResponsiveContainer>
              </div>
              <div className='mt-2'>
                {rows.map(row => (
                  <div key={row.date} className='mb-1 flex w-full justify-between text-sm'>
                    <Typography.Text>{row.date}</Typography.Text>
                    <Mask>
                      <ColoredFormattedNumber value={totalPerDay(row)} />
                    </Mask>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <Empty description='Não há dados a serem mostrados' />
          )}
        </div>
      </div>
    </Card>
  );
};

export default BarChart;
